import { Docx, CustomNode, convertNode, convertTextNode, pointsToHalfPoints, pointsToTwips } from '@tiptap-pro/extension-export-docx';
import { Editor } from '@tiptap/core';
import { JSONContent } from '@tiptap/react';
import { NumberedNodes, NumberedNodeType, getTypeDescription } from './numbering';
import { capitalizeFirstLetter } from '../../../utils/string';
import { Config } from '../../../env';
import { InternalLinkFormat } from './extensions/plugins/InternalLink';

const FIGCAPTION_FONT_SIZE_PX = '12'; // 9pt expressed as px for textStyle marks
const FIGCAPTION_FONT_SIZE_HP = pointsToHalfPoints(9); // 9pt in half-points for Docx objects
const LIST_ITEM_SPACING = { before: 0, after: 40 };

let listInstanceId = 1_000_000;

/**
 * Add a textStyle mark with smaller fontSize to all text nodes in a block tree.
 */
const applySmallFontSize = (blocks: JSONContent[]): void => {
  const walk = (node: JSONContent) => {
    if (node.type === 'text') {
      node.marks = [...(node.marks || []), { type: 'textStyle', attrs: { fontSize: FIGCAPTION_FONT_SIZE_PX } }];
    }
    node.content?.forEach(walk);
  };
  blocks.forEach(walk);
};

/**
 * Process a figcaption into block-level nodes with a bold label prepended
 * to the first paragraph. All text is rendered 2pt smaller than body text.
 * Figcaptions can contain (bulletList|orderedList|paragraph)+.
 */
const processFigcaption = (
  figcaption: JSONContent | undefined,
  processNode: (node: JSONContent) => JSONContent | JSONContent[],
  label: string,
): JSONContent[] => {
  const blocks = figcaption?.content ? figcaption.content.flatMap(processNode) : [];

  // Rename lists to small variants for smaller font in custom renderers
  for (const block of blocks) {
    if (block.type === 'customOrderedList') block.type = 'smallOrderedList';
    if (block.type === 'dashList') block.type = 'smallDashList';
  }

  if (label) {
    const first = blocks.find((b) => b.type === 'paragraph');
    const labelNode: JSONContent = { type: 'text', text: label + (first?.content?.length ? ' ' : ''), marks: [{ type: 'bold' }] };
    if (first) {
      first.content = [labelNode, ...(first.content || [])];
    } else {
      blocks.unshift({ type: 'paragraph', content: [labelNode] });
    }
  }

  applySmallFontSize(blocks);
  return blocks;
};

// --- LaTeX preprocessing for the docx engine's limited OMML parser ---
//
// The engine handles: \frac, \sqrt, \binom, \text, \begin{matrix}...\end{matrix},
// sub/superscripts, and a unicode symbol map (Greek letters, operators, etc.).
// Two engine bugs we must work around:
//   1. Regexes use [^}]* — braced args cannot contain nested braces
//   2. Unicode map prefix-matches \le from \left, corrupting \left/\right
//
// cleanLatex(latex, depth) walks the string and for each \command:
//   left/right      → strip command, keep delimiter char (avoids bug #2)
//   ENGINE_COMMANDS at depth 0  → keep for engine, clean args at depth 1
//   ENGINE_COMMANDS at depth >0 → linearize to avoid nested braces (bug #1)
//                                  \frac{a}{b} → (a)/(b), \sqrt{x} → √(x), etc.
//   STRIP_COMMANDS  → remove entirely (display hints with no visual output)
//   unknown with {  → unwrap: drop command, keep brace content
//   unknown without → pass through for engine's unicode symbol map

const ENGINE_COMMANDS = new Set(['frac', 'sqrt', 'binom', 'text', 'begin', 'end']);
const STRIP_COMMANDS = new Set(['displaystyle', 'textstyle', 'scriptstyle', 'scriptscriptstyle']);

const findClosingBrace = (s: string, start: number): number => {
  let depth = 0;
  for (let i = start; i < s.length; i++) {
    if (s[i] === '{') depth++;
    else if (s[i] === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
};

/** Flatten _{multi} → _multi so the engine's regex [^}]* can match full args. */
const flattenSubSup = (s: string): string => s.replace(/([_^])\{([^{}]*)\}/g, '$1$2');

/** Extract up to `count` braced args starting at `pos`, cleaning each at the given depth. */
const extractArgs = (s: string, pos: number, count: number, depth: number): { args: string[]; end: number } => {
  const args: string[] = [];
  let p = pos;
  while (p < s.length && s[p] === '{' && args.length < count) {
    const close = findClosingBrace(s, p);
    if (close === -1) break;
    args.push(cleanLatex(s.slice(p + 1, close), depth));
    p = close + 1;
  }
  return { args, end: p };
};

const cleanLatex = (latex: string, depth = 0): string => {
  let s = latex.replace(/\\[dtc]frac\b/g, '\\frac');
  s = s.replace(/\\(quad|qquad|enspace|thinspace|thickspace|medspace)\b/g, ' ');
  s = s.replace(/\\[,;:! ]/g, ' ');

  let out = '';
  let i = 0;
  while (i < s.length) {
    if (s[i] !== '\\') {
      out += s[i++];
      continue;
    }
    const m = s.slice(i).match(/^\\([a-zA-Z]+)/);
    if (!m) {
      out += s[i++];
      continue;
    }
    const cmd = m[1];
    const after = i + m[0].length;

    if (cmd === 'left' || cmd === 'right') {
      i = after; // strip command, delimiter char stays
    } else if (ENGINE_COMMANDS.has(cmd)) {
      i = after;
      const nArgs = cmd === 'frac' || cmd === 'binom' ? 2 : 1;
      const { args, end } = extractArgs(s, i, nArgs, depth === 0 ? 1 : depth);
      i = end;

      if (depth === 0) {
        // Keep for engine, with cleaned + flattened args
        out += m[0] + args.map((a) => `{${flattenSubSup(a)}}`).join('');
      } else {
        // Linearize — no braces allowed inside another command's args
        switch (cmd) {
          case 'frac': if (args.length === 2) out += `(${args[0]})/(${args[1]})`; break;
          case 'binom': if (args.length === 2) out += `(${args[0]},${args[1]})`; break;
          case 'sqrt': out += `√(${args[0] ?? ''})`; break;
          case 'text': out += args[0] ?? ''; break;
          // begin/end: drop command + arg name, walker handles content between them
        }
      }
    } else if (STRIP_COMMANDS.has(cmd)) {
      i = after;
    } else if (s[after] === '{') {
      const close = findClosingBrace(s, after);
      if (close !== -1) {
        out += cleanLatex(s.slice(after + 1, close), depth);
        i = close + 1;
      } else {
        out += m[0];
        i = after;
      }
    } else {
      out += m[0];
      i = after;
    }
  }

  return out.replace(/  +/g, ' ').trim();
};

/** Split text nodes containing $...$ into text and inlineMath nodes. */
const extractMathFromText = (nodes: JSONContent[]): JSONContent[] =>
  nodes.flatMap((node) => {
    if (node.type !== 'text' || !node.text?.includes('$')) return [node];
    const parts: JSONContent[] = [];
    const regex = /\$([^$]+)\$/g;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(node.text)) !== null) {
      if (match.index > last) parts.push({ type: 'text', text: node.text.slice(last, match.index), marks: node.marks });
      parts.push({ type: 'inlineMath', attrs: { latex: cleanLatex(match[1]) } });
      last = regex.lastIndex;
    }
    if (last < node.text.length) parts.push({ type: 'text', text: node.text.slice(last), marks: node.marks });
    return parts.length > 0 ? parts : [node];
  });

/**
 * Wrap Math elements inside a Paragraph in an oMathPara with left justification.
 * Without this, Word auto-centers math-only paragraphs ("display math" mode).
 */
const setMathLeftAligned = (p: Docx.Paragraph) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const root = (p as any).root as any[];
  for (let i = 0; i < root.length; i++) {
    if (root[i] instanceof Docx.Math) {
      root[i] = new Docx.BuilderElement({
        name: 'm:oMathPara',
        children: [
          new Docx.BuilderElement({
            name: 'm:oMathParaPr',
            children: [new Docx.BuilderElement({ name: 'm:jc', attributes: { val: { key: 'm:val', value: 'left' } } })],
          }),
          root[i],
        ],
      });
    }
  }
};

/**
 * Recursively process the document JSON, converting custom nodes
 * to standard Tiptap-compatible nodes that the export engine can handle.
 */
const preprocessDocument = (json: JSONContent, numberedNodes: NumberedNodes, config: Config, guideId: string): JSONContent => {
  const clone: JSONContent = JSON.parse(JSON.stringify(json));

  // Strip control characters that are invalid in XML (breaks Word)
  const sanitizeText = (node: JSONContent) => {
    if (node.text) node.text = node.text.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, '');
    node.content?.forEach(sanitizeText);
  };
  sanitizeText(clone);

  const figureLabel = (type: NumberedNodeType, id: string): string => {
    const n = numberedNodes.get(id);
    return n ? `${capitalizeFirstLetter(getTypeDescription(type))} ${n.numbering}` : '';
  };

  const processNode = (node: JSONContent): JSONContent | JSONContent[] => {
    // Inline atom nodes -> text nodes
    if (node.type === 'internalLink') {
      const n = numberedNodes.get(node.attrs?.id);
      const typeDesc = getTypeDescription(node.attrs?.type, node.attrs?.format as InternalLinkFormat);
      return { type: 'text', text: `${typeDesc} ${n?.numbering ?? ''}` };
    }
    if (node.type === 'guideLink') return { type: 'text', text: node.attrs?.docName || '' };
    if (node.type === 'standardLink') return { type: 'text', text: node.attrs?.standardName || '' };
    if (node.type === 'mathType') return { type: 'text', text: (node.attrs?.latexParts || []).join('') || '[Formel]' };

    // Heading -> store numbering in attrs (rendered with a tab separator in convertChild)
    if (node.type === 'heading') {
      const n = numberedNodes.get(node.attrs?.id);
      node.content = (node.content || []).flatMap(processNode);
      if (n) node.attrs = { ...node.attrs, headingNumber: n.numbering };
      return node;
    }

    // ImageFigure -> image + caption
    if (node.type === 'imageFigure') {
      const attrs = node.attrs || {};
      const result: JSONContent[] = [];
      if (attrs.smallId && attrs.figureId) {
        const url = `${config.apiUrl}/guides/${guideId}/figures/${attrs.figureId}/files/${attrs.smallId}/${attrs.smallFilename || 'image'}?type=small`;
        result.push({ type: 'image', attrs: { src: url } });
      }
      result.push(...processFigcaption(node.content?.[0], processNode, figureLabel('imageFigure', attrs.id)));
      return result.length > 0 ? result : [{ type: 'paragraph', content: [] }];
    }

    // TableFigure -> caption + table + caption
    if (node.type === 'tableFigure') {
      const attrs = node.attrs || {};
      const content = node.content || [];
      const result: JSONContent[] = [];

      const firstCaption = content[0];
      if (firstCaption?.type === 'figcaption') {
        result.push(...processFigcaption(firstCaption, processNode, figureLabel('tableFigure', attrs.id)));
      }

      const tableNode = content.find((c) => c.type === 'table');
      if (tableNode) {
        // processNode handles inline atom conversion in table cells recursively
        result.push(processNode(tableNode) as JSONContent);
      }

      const lastCaption = content[content.length - 1];
      if (lastCaption?.type === 'figcaption' && lastCaption !== firstCaption) {
        result.push(...processFigcaption(lastCaption, processNode, ''));
      }

      return result.length > 0 ? result : [{ type: 'paragraph', content: [] }];
    }

    // ChartFigure -> placeholder + caption
    if (node.type === 'chartFigure') {
      const attrs = node.attrs || {};
      return [
        { type: 'paragraph', content: [{ type: 'text', text: '[Diagram]', marks: [{ type: 'italic' }] }] },
        ...processFigcaption(
          node.content?.find((c) => c.type === 'figcaption'),
          processNode,
          figureLabel('chartFigure', attrs.id),
        ),
      ];
    }

    if (node.type === 'chart') return [];

    // App -> labeled paragraph
    if (node.type === 'app') {
      const attrs = node.attrs || {};
      const label = figureLabel('app', attrs.id);
      return { type: 'paragraph', content: [{ type: 'text', text: `${label} ${attrs.name || ''}`.trim(), marks: [{ type: 'bold' }] }] };
    }

    // Figcaption -> unwrap
    if (node.type === 'figcaption') return node.content ? node.content.flatMap(processNode) : [];

    // Table cells -> smaller font (same as figcaptions)
    if (node.type === 'tableCell' || node.type === 'tableHeader') {
      if (node.content) node.content = node.content.flatMap(processNode);
      applySmallFontSize(node.content || []);
      return node;
    }

    // Rename lists so they bypass the export engine's built-in handlers
    if (node.type === 'bulletList') node.type = 'dashList';
    if (node.type === 'orderedList') node.type = 'customOrderedList';

    // Default: recursively process children, extract $...$ math from text nodes
    if (node.content) {
      node.content = node.content.flatMap(processNode);
      node.content = extractMathFromText(node.content);
      // Convert attachment marks to links
      for (const child of node.content) {
        if (child.marks) {
          child.marks = child.marks.map((mark) =>
            mark.type === 'attachment'
              ? { type: 'link', attrs: { href: `${window.location.origin}${config.apiUrl}/guides/${guideId}/attachments/${mark.attrs?.attachmentId}/${mark.attrs?.filename}` } }
              : mark,
          );
        }
      }
    }
    return node;
  };

  if (clone.content) clone.content = clone.content.flatMap(processNode);
  return clone;
};

// --- Custom node renderers ---

/**
 * Render list items as Docx Paragraphs with proper numbering references.
 * Optional fontSize sets paragraph-level run properties (affects numbering markers too).
 */
const renderListItems = (
  items: JSONContent[],
  listType: 'dash' | 'ordered',
  level: number,
  instanceId: number,
  fontSize?: number,
): Docx.Paragraph[] => {
  const paragraphs: Docx.Paragraph[] = [];
  for (const item of items) {
    for (const child of item.content || []) {
      if (child.type === 'dashList' || child.type === 'smallDashList') {
        paragraphs.push(...renderListItems(child.content || [], 'dash', level + 1, instanceId, fontSize));
      } else if (child.type === 'customOrderedList' || child.type === 'smallOrderedList') {
        paragraphs.push(...renderListItems(child.content || [], 'ordered', level + 1, ++listInstanceId, fontSize));
      } else {
        const children = (child.content || [])
          .map((c) => (c.type === 'hardBreak' ? new Docx.TextRun({ text: '', break: 1 }) : convertTextNode(c)))
          .filter(Boolean);
        paragraphs.push(
          new Docx.Paragraph({
            children,
            ...(fontSize && { run: { size: fontSize } }),
            numbering: {
              reference: listType === 'dash' ? 'dash-list' : 'ordered-list',
              level,
              ...(listType === 'ordered' && { instance: instanceId }),
            },
            spacing: LIST_ITEM_SPACING,
          }),
        );
      }
    }
  }
  return paragraphs;
};

const customNodes: CustomNode[] = [
  { type: 'pageBreak', render: () => new Docx.Paragraph({ children: [new Docx.PageBreak()] }) },
  { type: 'dashList', render: (node) => renderListItems(node.content || [], 'dash', 0, 0) },
  { type: 'customOrderedList', render: (node) => renderListItems(node.content || [], 'ordered', 0, ++listInstanceId) },
  { type: 'smallDashList', render: (node) => renderListItems(node.content || [], 'dash', 0, 0, FIGCAPTION_FONT_SIZE_HP) },
  { type: 'smallOrderedList', render: (node) => renderListItems(node.content || [], 'ordered', 0, ++listInstanceId, FIGCAPTION_FONT_SIZE_HP) },
];

// --- Image max width ---

const IMAGE_MAX_WIDTH_PT = 520;

/**
 * Convert an image node to a Docx Paragraph, capping width at IMAGE_MAX_WIDTH_PT.
 */
const convertImageCapped = async (node: JSONContent): Promise<Docx.Paragraph> => {
  const src = node.attrs?.src;
  if (!src) return new Docx.Paragraph({ children: [] });

  const res = await fetch(src);
  if (!res.ok) return new Docx.Paragraph({ children: [] });

  const buffer = new Uint8Array(await res.arrayBuffer());
  const bmp = await createImageBitmap(new Blob([buffer]));
  let w = bmp.width * 0.75,
    h = bmp.height * 0.75;
  bmp.close();
  if (w > IMAGE_MAX_WIDTH_PT) {
    h *= IMAGE_MAX_WIDTH_PT / w;
    w = IMAGE_MAX_WIDTH_PT;
  }

  const ct = res.headers.get('content-type') || '';
  const type: 'png' | 'jpg' | 'gif' | 'bmp' =
    ct.includes('jpeg') || ct.includes('jpg') ? 'jpg' : ct.includes('gif') ? 'gif' : ct.includes('bmp') ? 'bmp' : 'png';

  return new Docx.Paragraph({
    children: [new Docx.ImageRun({ data: buffer, transformation: { width: w, height: h }, type })],
  });
};

// --- Table conversion ---

/**
 * Convert a table node using convertNode for each cell child,
 * so that all content types (images, lists, paragraphs, etc.) are supported.
 * The export engine's built-in convertTableCell only handles paragraphs.
 */
// Page: 11907 twips wide, left margin 1616 (1134 + 482 body indent), right margin 2268
const TABLE_WIDTH = 11907 - 1616 - 2268; // 8023 twips

const convertTableNode = async (tableNode: JSONContent): Promise<Docx.Table> => {
  const rows = tableNode.content || [];
  const totalColumns = Math.max(
    ...rows.map((row) => (row.content || []).reduce((cols, cell) => cols + Math.max(1, Number(cell.attrs?.colspan) || 1), 0)),
    1,
  );
  const columnWidthDxa = Math.floor(TABLE_WIDTH / totalColumns);

  const tableRows = await Promise.all(
    rows.map(async (row) => {
      const cells = await Promise.all(
        (row.content || []).map(async (cell) => {
          const converted = await Promise.all((cell.content || []).map((child) => convertNode({ node: child, customNodes })));
          const children = converted.flat().filter((r): r is Docx.Paragraph | Docx.Table => r instanceof Docx.Paragraph || r instanceof Docx.Table);
          if (children.length === 0) children.push(new Docx.Paragraph({ children: [] }));

          const colspan = Number(cell.attrs?.colspan) || 1;
          const isHeader = cell.type === 'tableHeader';
          return new Docx.TableCell({
            children,
            width: { size: columnWidthDxa * colspan, type: Docx.WidthType.DXA },
            margins: { top: 40, bottom: 40, left: 80, right: 80 },
            ...(colspan > 1 && { columnSpan: colspan }),
            ...(isHeader && { shading: { fill: 'E6E6E6', type: Docx.ShadingType.CLEAR, color: 'auto' } }),
          });
        }),
      );
      return new Docx.TableRow({ children: cells });
    }),
  );

  return new Docx.Table({
    rows: tableRows,
    width: { size: TABLE_WIDTH, type: Docx.WidthType.DXA },
    layout: Docx.TableLayoutType.FIXED,
  });
};

// --- Document construction ---

const listLevels = (format: (typeof Docx.LevelFormat)[keyof typeof Docx.LevelFormat], textFn: (i: number) => string): Docx.ILevelsOptions[] =>
  Array.from({ length: 9 }, (_, i) => ({
    level: i,
    format,
    text: textFn(i),
    alignment: Docx.AlignmentType.LEFT,
    style: { paragraph: { indent: { left: 220 + i * 360, hanging: 220 } } },
  }));

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Export the editor content to a .docx file.
 */
export const exportEditorToDocx = async (editor: Editor, config: Config, guideId: string, filename: string, docName: string, docTitle: string) => {
  const numberedNodes = (editor.storage['numberedNodes'] as NumberedNodes) ?? new Map();
  const processedJson = preprocessDocument(editor.getJSON(), numberedNodes, config, guideId);

  const isMathBlock = (n: JSONContent) =>
    n.type === 'paragraph' && n.content?.length && n.content.every((c) => c.type === 'inlineMath' || (c.type === 'text' && !c.text?.trim()));

  const HEADING_LEVELS = [Docx.HeadingLevel.HEADING_1, Docx.HeadingLevel.HEADING_2, Docx.HeadingLevel.HEADING_3];

  const convertChild = async (child: JSONContent) => {
    if (child.type === 'table') return convertTableNode(child);
    if (child.type === 'image') return convertImageCapped(child);
    if (child.type === 'heading') {
      const number = child.attrs?.headingNumber as string | undefined;
      const runs = (child.content || [])
        .map((c) => (c.type === 'hardBreak' ? new Docx.TextRun({ break: 1 }) : convertTextNode(c)))
        .filter((r): r is Docx.TextRun => r != null);
      return new Docx.Paragraph({
        heading: HEADING_LEVELS[Math.min((child.attrs?.level || 1) - 1, 2)],
        children: number ? [new Docx.TextRun({ children: [number, new Docx.Tab()] }), ...runs] : runs,
      });
    }
    const result = await convertNode({ node: child, customNodes });
    if (isMathBlock(child)) {
      const p = (Array.isArray(result) ? result[0] : result) as Docx.Paragraph;
      setMathLeftAligned(p);
    }
    return result;
  };

  const converted = await Promise.all((processedJson.content || []).map(convertChild));
  const children = converted.flat().filter((r): r is Docx.Paragraph | Docx.Table => r instanceof Docx.Paragraph || r instanceof Docx.Table);

  // Title paragraph at the top of the document
  const headerText = `${docName} ${docTitle}`.trim();
  if (headerText) {
    children.unshift(
      new Docx.Paragraph({
        style: 'GuideTitle',
        children: [new Docx.TextRun({ text: headerText })],
      }),
      new Docx.Paragraph({ children: [] }),
    );
  }

  const FONT = 'Arial';
  const doc = new Docx.Document({
    numbering: {
      config: [
        { reference: 'ordered-list', levels: listLevels(Docx.LevelFormat.DECIMAL, (i) => `%${i + 1}.`) },
        { reference: 'dash-list', levels: listLevels(Docx.LevelFormat.BULLET, () => '\u2013') },
      ],
    },
    styles: {
      paragraphStyles: [
        {
          id: 'Normal',
          name: 'Normal',
          run: { font: FONT, size: pointsToHalfPoints(11), color: '000000' },
          paragraph: {
            alignment: Docx.AlignmentType.LEFT,
            spacing: { after: 120, line: 260, lineRule: Docx.LineRuleType.AT_LEAST },
          },
        },
        {
          id: 'Heading1',
          name: 'heading 1',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { font: FONT, size: pointsToHalfPoints(14), bold: true },
          paragraph: {
            keepNext: true,
            spacing: { before: 480, after: 80, line: 300, lineRule: Docx.LineRuleType.AT_LEAST },
            indent: { hanging: 482 },
            border: {
              bottom: { style: Docx.BorderStyle.SINGLE, size: 2, space: 7, color: '808080' },
            },
          },
        },
        {
          id: 'Heading2',
          name: 'heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { font: FONT, size: pointsToHalfPoints(12), bold: true },
          paragraph: {
            keepNext: true,
            spacing: { before: 180, after: 0, line: 240 },
            indent: { hanging: 482 },
          },
        },
        {
          id: 'Heading3',
          name: 'heading 3',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { font: FONT, size: pointsToHalfPoints(11) },
          paragraph: {
            spacing: { after: 0 },
            indent: { hanging: 482 },
          },
        },
        {
          id: 'ListParagraph',
          name: 'List Paragraph',
          basedOn: 'Normal',
          quickFormat: true,
          run: { font: FONT, size: pointsToHalfPoints(11) },
          paragraph: { spacing: { before: 0, after: pointsToTwips(2), line: 240 } },
        },
        {
          id: 'GuideTitle',
          name: 'Guide Title',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { font: FONT, size: pointsToHalfPoints(18), bold: true },
          paragraph: { spacing: { before: 150, after: 600, line: 300, lineRule: Docx.LineRuleType.AT_LEAST }, indent: { left: -482 } },
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1134, right: 2268, bottom: 1134, left: 1616, header: 425, footer: 425 },
          },
        },
        headers: {
          default: new Docx.Header({
            children: [
              new Docx.Paragraph({
                alignment: Docx.AlignmentType.CENTER,
                children: [new Docx.TextRun({ text: `${docName} ${docTitle}`, size: pointsToHalfPoints(9), font: FONT })],
              }),
            ],
          }),
        },
        children,
      },
    ],
  });

  downloadBlob(await Docx.Packer.toBlob(doc), filename);
};
